import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';

import { LinkClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import methodsBanner from 'src/assets/img/methods-banner.webp';
import { Button } from 'src/components/Button';
import { NewsCard } from 'src/components/NewsCard';
import { defaultConfig } from 'src/config';

interface MethodsCardProps {
  hasSubscription: boolean;
}

const UTM_CAMPAIGN = 'Discover reMarkable Methods';
const UTM_SOURCE = 'front page - methods card';

export const MethodsCard = ({ hasSubscription }: MethodsCardProps) => {
  const { t } = useLingui();

  return (
    <NewsCard
      imageSources={[{ type: 'webp', url: methodsBanner }]}
      mediaAlt="A collection of thumbnail images showcasing templates and workbooks. Click to learn more on methods.remarkable.com."
      mediaLink={defaultConfig.MethodsUrl}
      headline={t`Methods`}
      openNewTab
      mediaOnClick={() => {
        tracker.trackEvent(
          new LinkClicked({
            component_location: ComponentLocations.FRONT_PAGE.METHODS_CARD,
            text: t`Discover reMarkable Methods`,
            link_source: defaultConfig.MethodsUrl,
          })
        );
        tracker.identify({
          myrm_utm_parameters: JSON.stringify({
            myrm_utm_source: UTM_SOURCE,
            myrm_utm_campaign: UTM_CAMPAIGN,
          }),
        });
      }}
    >
      <div className="flex flex-col gap-32">
        <div className="flex flex-col gap-16">
          <Typography className="m-0 text-left" variant="responsive-heading-md">
            <Trans>Explore templates and workbooks</Trans>
          </Typography>
          {hasSubscription ? (
            <Tag variant="success-light" className="whitespace-nowrap">
              <Trans>New</Trans>
            </Tag>
          ) : (
            <Tag variant="info-light" className="whitespace-nowrap">
              <Trans>Connect Feature</Trans>
            </Tag>
          )}
          <Typography
            className="m-0 text-left"
            textWrap="pretty"
            variant="body-md"
          >
            <Trans>
              Lead meetings, take notes, and solve complex problems with
              exclusive templates from Methods, our community of expert
              thinkers. Ready on your paper tablet with a single click.
            </Trans>
          </Typography>
        </div>

        <Button
          as="a"
          variant="secondary"
          to={defaultConfig.MethodsUrl}
          analytics={{
            location: ComponentLocations.FRONT_PAGE.METHODS_CARD,
            action: 'go to methods',
          }}
          onClick={() => {
            tracker.identify({
              myrm_utm_parameters: JSON.stringify({
                myrm_utm_source: UTM_SOURCE,
                myrm_utm_campaign: UTM_CAMPAIGN,
              }),
            });
          }}
        >
          <span>
            <Trans>Learn more</Trans>
          </span>
          <ArrowRight />
        </Button>
      </div>
    </NewsCard>
  );
};
